import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PhoneLink from '../components/PhoneLink';
import { useAuth } from '../context/AuthContext';
import { invoiceService, userService } from '../services/api';
import { User, Invoice } from '../types';
import { defaultPaymentYear, getPaymentYearOptions } from '../utils/paymentYears';
import { toast } from 'react-toastify';
import './CreateInvoice.css';

const CreateInvoice: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<Partial<Invoice>>({
    paymentMonth: new Date().toLocaleString('default', { month: 'long' }),
    paymentYear: defaultPaymentYear(),
    paymentType: 'RENT',
    paidAmount: '',
    balanceAmount: '',
    discount: '',
    note: '',
    dueDate: '',
  });
  const [loading, setLoading] = useState(false);
  const [searchPhone, setSearchPhone] = useState('');

  useEffect(() => {
    // Optional prefetch hook: keep here if we later add a user dropdown.
    // Currently, we rely on phone search to select a user.
  }, []);

  const handleSearchUser = async () => {
    if (!searchPhone) return;
    try {
      const foundUser = await userService.getUserByPhone(searchPhone);
      setSelectedUser(foundUser);
      setFormData(prev => ({
        ...prev,
        paymentMonth: new Date().toLocaleString('default', { month: 'long' }),
        paymentYear: defaultPaymentYear(),
      }));
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'User not found');
      setSelectedUser(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser || !user) {
      toast.error('Please select a user');
      return;
    }

    setLoading(true);
    try {
      await invoiceService.createInvoice(selectedUser.id, user.id, formData);
      toast.success('Invoice created successfully!');
      navigate('/invoices');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create invoice');
    } finally {
      setLoading(false);
    }
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const years = getPaymentYearOptions();

  return (
    <div className="create-invoice">
      <h1>Create Invoice</h1>

      <div className="invoice-form-container">
        <div className="user-search-section">
          <h3>Search User</h3>
          <div className="search-box">
            <input
              type="text"
              placeholder="Enter phone number"
              value={searchPhone}
              onChange={(e) => setSearchPhone(e.target.value)}
            />
            <button type="button" onClick={handleSearchUser}>
              Search
            </button>
          </div>

          {selectedUser && (
            <div className="user-info-card">
              <h4>Selected User</h4>
              <p><strong>Name:</strong> {selectedUser.username}</p>
              <p>
                <strong>Phone:</strong> <PhoneLink phone={selectedUser.phoneNumber} />
              </p>
              <p><strong>Room:</strong> {selectedUser.floor}-{selectedUser.room}-{selectedUser.bed}</p>
              <p><strong>Rent:</strong> ₹{selectedUser.rent || '0'}</p>
              <p><strong>Advance:</strong> ₹{selectedUser.advance || '0'}</p>
            </div>
          )}
        </div>

        {selectedUser && (
          <form onSubmit={handleSubmit} className="invoice-form">
            <div className="form-row">
              <div className="form-group">
                <label>Payment Month</label>
                <select
                  value={formData.paymentMonth}
                  onChange={(e) => setFormData({ ...formData, paymentMonth: e.target.value })}
                  required
                >
                  {months.map(month => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Payment Year</label>
                <select
                  value={formData.paymentYear}
                  onChange={(e) => setFormData({ ...formData, paymentYear: e.target.value })}
                  required
                >
                  {years.map(year => (
                    <option key={year} value={year.toString()}>{year}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Payment Type</label>
                <select
                  value={formData.paymentType}
                  onChange={(e) => setFormData({ ...formData, paymentType: e.target.value as 'RENT' | 'ADVANCE' })}
                  required
                >
                  <option value="RENT">Rent</option>
                  <option value="ADVANCE">Advance</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Paid Amount (₹)</label>
                <input
                  type="number"
                  value={formData.paidAmount}
                  onChange={(e) => setFormData({ ...formData, paidAmount: e.target.value })}
                  required
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="form-group">
                <label>Balance Amount (₹)</label>
                <input
                  type="number"
                  value={formData.balanceAmount}
                  onChange={(e) => setFormData({ ...formData, balanceAmount: e.target.value })}
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="form-group">
                <label>Discount (₹)</label>
                <input
                  type="number"
                  value={formData.discount}
                  onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Due Date</label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Note</label>
              <textarea
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                rows={3}
                placeholder="Additional notes..."
              />
            </div>

            <div className="form-actions">
              <button type="button" onClick={() => navigate('/invoices')} className="btn-secondary">
                Cancel
              </button>
              <button type="submit" disabled={loading} className="btn-primary">
                {loading ? 'Creating...' : 'Create Invoice'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreateInvoice;

